import { Capacitor, registerPlugin } from '@capacitor/core'

import { supabase } from './supabase'
import type {
  AppleHealthAggregation,
  AppleHealthAuthorizationStatus,
  AppleHealthConnection,
  AppleHealthConnectionInput,
  AppleHealthDailySummary,
  AppleHealthDailySummaryInput,
  AppleHealthMetricDefinition,
  AppleHealthMetricGroup,
  AppleHealthMetricKey,
  AppleHealthNativeAvailabilityResult,
  AppleHealthNativePermissionResult,
  AppleHealthNativeSyncResult,
  AppleHealthSample,
  AppleHealthSampleInput,
  AppleHealthSampleKind,
  AppleHealthSupportState,
  AppleHealthSyncRun,
  AppleHealthSyncRunStatus
} from '@/types/health'

const APPLE_HEALTH_CONNECTION_SOURCE = 'Apple Health'
const APPLE_HEALTH_RELATIONS = [
  'apple_health_connections',
  'apple_health_samples',
  'apple_health_daily_summaries',
  'apple_health_sync_runs'
] as const

type AppleHealthRelationName = (typeof APPLE_HEALTH_RELATIONS)[number]

type NativeAppleHealthPermissionPayload = {
  metrics?: AppleHealthMetricKey[]
}

type NativeAppleHealthSyncPayload = {
  metrics?: AppleHealthMetricKey[]
  lastCursor?: string | null
}

type NativeAppleHealthSyncResponse = Partial<AppleHealthNativeSyncResult> & {
  samples?: AppleHealthSampleInput[]
  daily_summaries?: AppleHealthDailySummaryInput[]
}

type AppleHealthBridgePlugin = {
  isAvailable: () => Promise<AppleHealthNativeAvailabilityResult>
  requestPermissions: (
    payload: NativeAppleHealthPermissionPayload
  ) => Promise<Partial<AppleHealthNativePermissionResult>>
  sync: (payload: NativeAppleHealthSyncPayload) => Promise<NativeAppleHealthSyncResponse>
}

type AppleHealthListOptions = {
  metricKeys?: AppleHealthMetricKey[]
  dateFrom?: string
  dateTo?: string
  limit?: number
}

type AppleHealthSyncOptions = {
  connectionId?: string | null
  metricKeys?: AppleHealthMetricKey[]
  lastCursor?: string | null
}

const AppleHealthBridge = registerPlugin<AppleHealthBridgePlugin>('AppleHealthBridge')

const APPLE_HEALTH_METRICS: AppleHealthMetricDefinition[] = [
  {
    key: 'sleep_analysis',
    label: 'Sommeil detaille',
    group: 'sleep',
    sample_kind: 'category',
    default_unit: null,
    healthkit_identifier: 'HKCategoryTypeIdentifierSleepAnalysis',
    description: 'Phases et segments de sommeil detectes par Apple Health.',
    priority: 'high'
  },
  {
    key: 'sleep_duration',
    label: 'Duree de sommeil',
    group: 'sleep',
    sample_kind: 'derived',
    default_unit: 'h',
    healthkit_identifier: 'derived.sleep.duration',
    description: 'Resume journalier du temps de sommeil utile pour les correlations.',
    priority: 'high'
  },
  {
    key: 'step_count',
    label: 'Pas',
    group: 'activity',
    sample_kind: 'quantity',
    default_unit: 'count',
    healthkit_identifier: 'HKQuantityTypeIdentifierStepCount',
    description: 'Volume de marche au quotidien.',
    priority: 'high'
  },
  {
    key: 'walking_running_distance',
    label: 'Distance marche course',
    group: 'activity',
    sample_kind: 'quantity',
    default_unit: 'km',
    healthkit_identifier: 'HKQuantityTypeIdentifierDistanceWalkingRunning',
    description: 'Distance utile pour suivre les baisses de forme et la reprise.',
    priority: 'medium'
  },
  {
    key: 'active_energy_burned',
    label: 'Energie active',
    group: 'activity',
    sample_kind: 'quantity',
    default_unit: 'kcal',
    healthkit_identifier: 'HKQuantityTypeIdentifierActiveEnergyBurned',
    description: 'Depense active journaliere.',
    priority: 'medium'
  },
  {
    key: 'exercise_time',
    label: 'Temps exercice',
    group: 'activity',
    sample_kind: 'quantity',
    default_unit: 'min',
    healthkit_identifier: 'HKQuantityTypeIdentifierAppleExerciseTime',
    description: 'Temps d exercice total en minutes.',
    priority: 'medium'
  },
  {
    key: 'heart_rate',
    label: 'Frequence cardiaque',
    group: 'heart',
    sample_kind: 'quantity',
    default_unit: 'bpm',
    healthkit_identifier: 'HKQuantityTypeIdentifierHeartRate',
    description: 'Mesures cardiaques courantes pour suivre les variations.',
    priority: 'high'
  },
  {
    key: 'resting_heart_rate',
    label: 'Frequence cardiaque au repos',
    group: 'heart',
    sample_kind: 'quantity',
    default_unit: 'bpm',
    healthkit_identifier: 'HKQuantityTypeIdentifierRestingHeartRate',
    description: 'Tres utile pour reperer fatigue, stress ou inflammation.',
    priority: 'high'
  },
  {
    key: 'heart_rate_variability_sdnn',
    label: 'Variabilite cardiaque',
    group: 'heart',
    sample_kind: 'quantity',
    default_unit: 'ms',
    healthkit_identifier: 'HKQuantityTypeIdentifierHeartRateVariabilitySDNN',
    description: 'Indicateur de recuperation et de stress physiologique.',
    priority: 'high'
  },
  {
    key: 'respiratory_rate',
    label: 'Frequence respiratoire',
    group: 'vitals',
    sample_kind: 'quantity',
    default_unit: 'breaths/min',
    healthkit_identifier: 'HKQuantityTypeIdentifierRespiratoryRate',
    description: 'Vital nocturne utile avec le sommeil et la fatigue.',
    priority: 'medium'
  },
  {
    key: 'oxygen_saturation',
    label: 'Oxygene sanguin',
    group: 'vitals',
    sample_kind: 'quantity',
    default_unit: '%',
    healthkit_identifier: 'HKQuantityTypeIdentifierOxygenSaturation',
    description: 'Saturation mesuree via l ecosysteme Apple quand disponible.',
    priority: 'medium'
  },
  {
    key: 'apple_sleeping_wrist_temperature',
    label: 'Temperature du poignet',
    group: 'vitals',
    sample_kind: 'quantity',
    default_unit: 'degC',
    healthkit_identifier: 'HKQuantityTypeIdentifierAppleSleepingWristTemperature',
    description: 'Variation nocturne interessante pour cycles et inflammations.',
    priority: 'high'
  },
  {
    key: 'body_temperature',
    label: 'Temperature corporelle',
    group: 'body',
    sample_kind: 'quantity',
    default_unit: 'degC',
    healthkit_identifier: 'HKQuantityTypeIdentifierBodyTemperature',
    description: 'Temperature manuelle si disponible dans Apple Health.',
    priority: 'medium'
  },
  {
    key: 'body_mass',
    label: 'Poids',
    group: 'body',
    sample_kind: 'quantity',
    default_unit: 'kg',
    healthkit_identifier: 'HKQuantityTypeIdentifierBodyMass',
    description: 'Suivi pondral lie aux symptomes et traitements.',
    priority: 'medium'
  },
  {
    key: 'body_fat_percentage',
    label: 'Masse grasse',
    group: 'body',
    sample_kind: 'quantity',
    default_unit: '%',
    healthkit_identifier: 'HKQuantityTypeIdentifierBodyFatPercentage',
    description: 'Si balance compatible, donne plus de contexte corporel.',
    priority: 'low'
  },
  {
    key: 'menstrual_flow',
    label: 'Flux menstruel',
    group: 'cycle',
    sample_kind: 'category',
    default_unit: null,
    healthkit_identifier: 'HKCategoryTypeIdentifierMenstrualFlow',
    description: 'Tres utile pour l endometriose et les correlations douleur cycle.',
    priority: 'high'
  },
  {
    key: 'intermenstrual_bleeding',
    label: 'Saignements hors regles',
    group: 'cycle',
    sample_kind: 'category',
    default_unit: null,
    healthkit_identifier: 'HKCategoryTypeIdentifierIntermenstrualBleeding',
    description: 'Permet de documenter les episodes de spotting ou saignements.',
    priority: 'high'
  },
  {
    key: 'cervical_mucus_quality',
    label: 'Glaire cervicale',
    group: 'cycle',
    sample_kind: 'category',
    default_unit: null,
    healthkit_identifier: 'HKCategoryTypeIdentifierCervicalMucusQuality',
    description: 'Contexte supplementaire de suivi du cycle.',
    priority: 'low'
  },
  {
    key: 'ovulation_test_result',
    label: 'Test d ovulation',
    group: 'cycle',
    sample_kind: 'category',
    default_unit: null,
    healthkit_identifier: 'HKCategoryTypeIdentifierOvulationTestResult',
    description: 'Resultats de tests relies au cycle si utilises.',
    priority: 'low'
  },
  {
    key: 'mindful_session',
    label: 'Sessions mindfulness',
    group: 'mindfulness',
    sample_kind: 'category',
    default_unit: 'min',
    healthkit_identifier: 'HKCategoryTypeIdentifierMindfulSession',
    description: 'Pour lier stress, douleur et recuperation.',
    priority: 'low'
  },
  {
    key: 'electrocardiogram',
    label: 'ECG',
    group: 'heart',
    sample_kind: 'electrocardiogram',
    default_unit: null,
    healthkit_identifier: 'HKElectrocardiogramType',
    description: 'Lectures ECG Apple Watch en lecture seule.',
    priority: 'medium'
  },
  {
    key: 'workout',
    label: 'Entrainements',
    group: 'activity',
    sample_kind: 'workout',
    default_unit: null,
    healthkit_identifier: 'HKWorkoutType',
    description: 'Resume des seances sportives en plus des pas et calories.',
    priority: 'medium'
  }
]

export const APPLE_HEALTH_GROUP_LABELS: Record<AppleHealthMetricGroup, string> = {
  sleep: 'Sommeil',
  heart: 'Coeur',
  activity: 'Activite',
  vitals: 'Vitals',
  body: 'Corps',
  cycle: 'Cycle',
  mindfulness: 'Mindfulness'
}

function buildErrorMessage(prefix: string, error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  return `${prefix}: ${message}`
}

function normalizeMetricKeys(metricKeys?: AppleHealthMetricKey[]) {
  const source = metricKeys && metricKeys.length > 0 ? metricKeys : APPLE_HEALTH_METRICS.map((metric) => metric.key)
  return [...new Set(source)]
}

function normalizeStoredMetricKeys(metricKeys?: AppleHealthMetricKey[]) {
  if (!metricKeys || metricKeys.length === 0) {
    return [] as AppleHealthMetricKey[]
  }

  return [...new Set(metricKeys)]
}

function expandRequestedMetricKeys(metricKeys?: AppleHealthMetricKey[]) {
  const expanded = new Set(normalizeMetricKeys(metricKeys))

  if (expanded.has('sleep_duration')) {
    expanded.add('sleep_analysis')
  }

  return [...expanded]
}

function normalizeDateTime(value?: string | null) {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

function normalizeDate(value?: string | null) {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.toISOString().slice(0, 10)
}

function normalizeSampleKind(value: unknown): AppleHealthSampleKind {
  return value === 'category' ||
    value === 'workout' ||
    value === 'electrocardiogram' ||
    value === 'derived'
    ? value
    : 'quantity'
}

function normalizeAuthorizationStatus(value: unknown): AppleHealthAuthorizationStatus {
  return value === 'granted' ||
    value === 'partial' ||
    value === 'denied' ||
    value === 'revoked' ||
    value === 'error'
    ? value
    : 'pending'
}

function normalizeSyncStatus(value: unknown): AppleHealthSyncRunStatus {
  return value === 'completed' || value === 'partial' || value === 'failed' ? value : 'running'
}

function normalizeAggregation(value: unknown): AppleHealthAggregation {
  return value === 'daily_average' ||
    value === 'daily_min' ||
    value === 'daily_max' ||
    value === 'latest' ||
    value === 'derived'
    ? value
    : 'daily_total'
}

function normalizeObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function relationListContains(message: string, relationNames: readonly string[]) {
  return relationNames.some((relationName) => message.includes(relationName))
}

function isMissingAppleHealthRelationError(
  error: unknown,
  relationNames: readonly AppleHealthRelationName[] = APPLE_HEALTH_RELATIONS
) {
  if (!error || typeof error !== 'object') {
    return false
  }

  const code = String((error as { code?: unknown }).code || '')
  const message = String((error as { message?: unknown }).message || '')

  if (code !== 'PGRST205' && code !== '42P01') {
    return false
  }

  return relationListContains(message, relationNames)
}

async function getLatestSyncCursor(patientId: string) {
  const { data, error } = await supabase
    .from('apple_health_sync_runs')
    .select('last_cursor')
    .eq('patient_id', patientId)
    .order('sync_started_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_sync_runs'])) {
      return null
    }

    throw new Error(buildErrorMessage('Impossible de charger le dernier curseur Apple Health', error))
  }

  return typeof data?.last_cursor === 'string' ? data.last_cursor : null
}

function normalizeConnectionInput(input: AppleHealthConnectionInput) {
  return {
    patient_id: input.patient_id,
    source_name: input.source_name || APPLE_HEALTH_CONNECTION_SOURCE,
    platform: input.platform || 'ios',
    device_name: input.device_name || null,
    watch_name: input.watch_name || null,
    ios_app_version: input.ios_app_version || null,
    authorization_status: normalizeAuthorizationStatus(input.authorization_status),
    granted_metrics: normalizeStoredMetricKeys(input.granted_metrics),
    revoked_metrics: normalizeStoredMetricKeys(input.revoked_metrics),
    last_authorized_at: normalizeDateTime(input.last_authorized_at),
    last_sync_at: normalizeDateTime(input.last_sync_at),
    metadata: normalizeObject(input.metadata)
  }
}

function normalizeSampleInput(input: AppleHealthSampleInput): AppleHealthSampleInput {
  const startAt = normalizeDateTime(input.start_at) || new Date().toISOString()
  const endAt = normalizeDateTime(input.end_at) || startAt
  const localDate = normalizeDate(input.local_date || startAt) || startAt.slice(0, 10)

  return {
    patient_id: input.patient_id,
    connection_id: input.connection_id || null,
    metric_key: input.metric_key,
    sample_kind: normalizeSampleKind(input.sample_kind),
    hk_uuid: input.hk_uuid || null,
    source_bundle_id: input.source_bundle_id || null,
    source_name: input.source_name || null,
    unit: input.unit || null,
    value_numeric:
      typeof input.value_numeric === 'number' && Number.isFinite(input.value_numeric)
        ? input.value_numeric
        : null,
    value_text: input.value_text || null,
    start_at: startAt,
    end_at: endAt,
    local_date: localDate,
    timezone: input.timezone || null,
    recorded_at: normalizeDateTime(input.recorded_at),
    hk_metadata: normalizeObject(input.hk_metadata)
  }
}

function normalizeDailySummaryInput(input: AppleHealthDailySummaryInput): AppleHealthDailySummaryInput {
  return {
    patient_id: input.patient_id,
    connection_id: input.connection_id || null,
    summary_date: normalizeDate(input.summary_date) || new Date().toISOString().slice(0, 10),
    metric_key: input.metric_key,
    aggregation: normalizeAggregation(input.aggregation),
    unit: input.unit || null,
    value_numeric:
      typeof input.value_numeric === 'number' && Number.isFinite(input.value_numeric)
        ? input.value_numeric
        : null,
    value_text: input.value_text || null,
    sample_count:
      typeof input.sample_count === 'number' && Number.isFinite(input.sample_count)
        ? Math.max(0, Math.round(input.sample_count))
        : 0,
    summary_payload: normalizeObject(input.summary_payload)
  }
}

async function getExistingSampleKeys(samples: AppleHealthSampleInput[]) {
  const existingKeys = new Set<string>()
  const grouped = new Map<string, Set<string>>()

  for (const sample of samples) {
    if (!sample.hk_uuid) {
      continue
    }

    const bucket = grouped.get(sample.patient_id) || new Set<string>()
    bucket.add(sample.hk_uuid)
    grouped.set(sample.patient_id, bucket)
  }

  for (const [patientId, hkUuids] of grouped.entries()) {
    const { data, error } = await supabase
      .from('apple_health_samples')
      .select('patient_id, hk_uuid')
      .eq('patient_id', patientId)
      .in('hk_uuid', [...hkUuids])

    if (error) {
      if (isMissingAppleHealthRelationError(error, ['apple_health_samples'])) {
        return existingKeys
      }

      throw new Error(buildErrorMessage('Impossible de verifier les doublons Apple Health', error))
    }

    for (const row of data || []) {
      if (row.hk_uuid) {
        existingKeys.add(`${row.patient_id}:${row.hk_uuid}`)
      }
    }
  }

  return existingKeys
}

export function getAppleHealthMetricCatalog() {
  return [...APPLE_HEALTH_METRICS]
}

export function getAppleHealthMetricsByGroup() {
  return APPLE_HEALTH_METRICS.reduce<Record<AppleHealthMetricGroup, AppleHealthMetricDefinition[]>>(
    (groups, metric) => {
      groups[metric.group].push(metric)
      return groups
    },
    {
      activity: [],
      sleep: [],
      heart: [],
      vitals: [],
      body: [],
      cycle: [],
      mindfulness: []
    }
  )
}

export function detectAppleHealthSupport(): AppleHealthSupportState {
  const platform = typeof window === 'undefined' ? 'web' : Capacitor.getPlatform()
  const pluginAvailable = typeof window !== 'undefined' && Capacitor.isPluginAvailable('AppleHealthBridge')

  if (platform === 'ios' && pluginAvailable) {
    return {
      available: true,
      mode: 'ios-native-ready',
      reason: 'Le wrapper iPhone MedTrack est detecte avec un bridge natif Apple Health.'
    }
  }

  if (platform === 'web') {
    return {
      available: false,
      mode: 'web',
      reason: 'Apple Health sera disponible dans l app iPhone MedTrack, pas dans le navigateur.'
    }
  }

  return {
    available: false,
    mode: 'unsupported',
    reason:
      platform === 'ios'
        ? 'Le wrapper iPhone est detecte mais le bridge natif HealthKit n est pas encore installe.'
        : 'Apple Health est disponible uniquement dans l app iPhone MedTrack.'
  }
}

export async function getAppleHealthNativeAvailability() {
  const platform = typeof window === 'undefined' ? 'web' : Capacitor.getPlatform()

  if (platform !== 'ios' || !Capacitor.isPluginAvailable('AppleHealthBridge')) {
    return {
      available: false,
      platform,
      reason:
        platform === 'web'
          ? 'Bridge HealthKit indisponible dans le navigateur.'
          : 'Bridge HealthKit non detecte pour cette plateforme.'
    } satisfies AppleHealthNativeAvailabilityResult
  }

  try {
    return await AppleHealthBridge.isAvailable()
  } catch (error) {
    return {
      available: false,
      platform,
      reason: error instanceof Error ? error.message : 'Bridge HealthKit indisponible.'
    } satisfies AppleHealthNativeAvailabilityResult
  }
}

export async function getAppleHealthConnection(patientId: string) {
  const { data, error } = await supabase
    .from('apple_health_connections')
    .select('*')
    .eq('patient_id', patientId)
    .eq('source_name', APPLE_HEALTH_CONNECTION_SOURCE)
    .maybeSingle()

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_connections'])) {
      return null
    }

    throw new Error(buildErrorMessage('Impossible de charger la connexion Apple Health', error))
  }

  return (data as AppleHealthConnection | null) ?? null
}

export async function listAppleHealthDailySummaries(patientId: string, options: AppleHealthListOptions = {}) {
  let query = supabase
    .from('apple_health_daily_summaries')
    .select('*')
    .eq('patient_id', patientId)
    .order('summary_date', { ascending: false })
    .limit(options.limit || 120)

  if (options.metricKeys && options.metricKeys.length > 0) {
    query = query.in('metric_key', options.metricKeys)
  }

  if (options.dateFrom) {
    query = query.gte('summary_date', options.dateFrom)
  }

  if (options.dateTo) {
    query = query.lte('summary_date', options.dateTo)
  }

  const { data, error } = await query

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_daily_summaries'])) {
      return [] as AppleHealthDailySummary[]
    }

    throw new Error(buildErrorMessage('Impossible de charger les resumes Apple Health', error))
  }

  return (data as AppleHealthDailySummary[]) || []
}

export async function listAppleHealthSamples(patientId: string, options: AppleHealthListOptions = {}) {
  let query = supabase
    .from('apple_health_samples')
    .select('*')
    .eq('patient_id', patientId)
    .order('start_at', { ascending: false })
    .limit(options.limit || 250)

  if (options.metricKeys && options.metricKeys.length > 0) {
    query = query.in('metric_key', options.metricKeys)
  }

  if (options.dateFrom) {
    query = query.gte('start_at', options.dateFrom)
  }

  if (options.dateTo) {
    query = query.lte('end_at', options.dateTo)
  }

  const { data, error } = await query

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_samples'])) {
      return [] as AppleHealthSample[]
    }

    throw new Error(buildErrorMessage('Impossible de charger les echantillons Apple Health', error))
  }

  return (data as AppleHealthSample[]) || []
}

export async function upsertAppleHealthConnection(input: AppleHealthConnectionInput) {
  const payload = normalizeConnectionInput(input)

  const { data, error } = await supabase
    .from('apple_health_connections')
    .upsert(payload, { onConflict: 'patient_id,source_name' })
    .select('*')
    .single()

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_connections'])) {
      return null
    }

    throw new Error(buildErrorMessage('Impossible d enregistrer la connexion Apple Health', error))
  }

  return data as AppleHealthConnection
}

export async function insertAppleHealthSamples(samples: AppleHealthSampleInput[]) {
  if (samples.length === 0) {
    return [] as AppleHealthSample[]
  }

  const normalizedSamples = samples.map(normalizeSampleInput)
  const dedupedByPayload = new Map<string, AppleHealthSampleInput>()

  for (const sample of normalizedSamples) {
    const key = sample.hk_uuid
      ? `hk:${sample.patient_id}:${sample.hk_uuid}`
      : `raw:${sample.patient_id}:${sample.metric_key}:${sample.start_at}:${sample.end_at}:${sample.value_numeric ?? sample.value_text ?? ''}`

    if (!dedupedByPayload.has(key)) {
      dedupedByPayload.set(key, sample)
    }
  }

  const deduped = [...dedupedByPayload.values()]
  const existingKeys = await getExistingSampleKeys(deduped)
  const insertable = deduped.filter(
    (sample) => !sample.hk_uuid || !existingKeys.has(`${sample.patient_id}:${sample.hk_uuid}`)
  )

  if (insertable.length === 0) {
    return [] as AppleHealthSample[]
  }

  const { data, error } = await supabase
    .from('apple_health_samples')
    .insert(insertable)
    .select('*')

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_samples'])) {
      return [] as AppleHealthSample[]
    }

    throw new Error(buildErrorMessage('Impossible d inserer les echantillons Apple Health', error))
  }

  return (data as AppleHealthSample[]) || []
}

export async function upsertAppleHealthDailySummaries(summaries: AppleHealthDailySummaryInput[]) {
  if (summaries.length === 0) {
    return [] as AppleHealthDailySummary[]
  }

  const normalizedSummaries = summaries.map(normalizeDailySummaryInput)
  const { data, error } = await supabase
    .from('apple_health_daily_summaries')
    .upsert(normalizedSummaries, {
      onConflict: 'patient_id,summary_date,metric_key,aggregation'
    })
    .select('*')

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_daily_summaries'])) {
      return [] as AppleHealthDailySummary[]
    }

    throw new Error(buildErrorMessage('Impossible d enregistrer les resumes Apple Health', error))
  }

  return (data as AppleHealthDailySummary[]) || []
}

export async function startAppleHealthSyncRun(
  patientId: string,
  requestedMetricKeys: AppleHealthMetricKey[],
  connectionId?: string | null
) {
  const { data, error } = await supabase
    .from('apple_health_sync_runs')
    .insert({
      patient_id: patientId,
      connection_id: connectionId || null,
      status: 'running',
      requested_metric_keys: normalizeMetricKeys(requestedMetricKeys),
      metadata: {}
    })
    .select('*')
    .single()

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_sync_runs'])) {
      return null
    }

    throw new Error(buildErrorMessage('Impossible de creer le log de synchronisation Apple Health', error))
  }

  return data as AppleHealthSyncRun
}

export async function finishAppleHealthSyncRun(
  runId: string,
  updates: {
    status: AppleHealthSyncRunStatus
    importedSamples?: number
    importedSummaries?: number
    lastCursor?: string | null
    errorMessage?: string | null
    metadata?: Record<string, unknown>
  }
) {
  const { data, error } = await supabase
    .from('apple_health_sync_runs')
    .update({
      status: normalizeSyncStatus(updates.status),
      imported_samples:
        typeof updates.importedSamples === 'number' && Number.isFinite(updates.importedSamples)
          ? Math.max(0, Math.round(updates.importedSamples))
          : 0,
      imported_summaries:
        typeof updates.importedSummaries === 'number' && Number.isFinite(updates.importedSummaries)
          ? Math.max(0, Math.round(updates.importedSummaries))
          : 0,
      last_cursor: updates.lastCursor || null,
      error_message: updates.errorMessage || null,
      sync_completed_at: new Date().toISOString(),
      metadata: normalizeObject(updates.metadata)
    })
    .eq('id', runId)
    .select('*')
    .single()

  if (error) {
    if (isMissingAppleHealthRelationError(error, ['apple_health_sync_runs'])) {
      return null
    }

    throw new Error(buildErrorMessage('Impossible de finaliser la synchronisation Apple Health', error))
  }

  return data as AppleHealthSyncRun
}

const DAILY_TOTAL_METRICS = new Set<AppleHealthMetricKey>([
  'step_count',
  'walking_running_distance',
  'active_energy_burned',
  'exercise_time',
  'mindful_session',
  'workout'
])

const DAILY_AVERAGE_METRICS = new Set<AppleHealthMetricKey>([
  'heart_rate',
  'respiratory_rate',
  'oxygen_saturation',
  'apple_sleeping_wrist_temperature',
  'body_temperature'
])

const DAILY_LATEST_METRICS = new Set<AppleHealthMetricKey>([
  'resting_heart_rate',
  'heart_rate_variability_sdnn',
  'body_mass',
  'body_fat_percentage',
  'menstrual_flow',
  'intermenstrual_bleeding',
  'cervical_mucus_quality',
  'ovulation_test_result',
  'electrocardiogram'
])

function getSampleNumericValue(sample: AppleHealthSampleInput) {
  return typeof sample.value_numeric === 'number' && Number.isFinite(sample.value_numeric)
    ? sample.value_numeric
    : null
}

function isSleepAsleepSample(sample: AppleHealthSampleInput) {
  const label = String(sample.value_text || '').toLowerCase()

  return label.includes('asleep') || label.includes('core') || label.includes('deep') || label.includes('rem')
}

function buildDerivedDailySummaries(
  patientId: string,
  connectionId: string | null,
  samples: AppleHealthSampleInput[]
) {
  const grouped = new Map<string, AppleHealthSampleInput[]>()

  for (const sample of samples) {
    const summaryDate = sample.local_date
    const key = `${summaryDate}:${sample.metric_key}`
    const bucket = grouped.get(key) || []
    bucket.push(sample)
    grouped.set(key, bucket)
  }

  const summaries: AppleHealthDailySummaryInput[] = []

  for (const [groupKey, groupSamples] of grouped.entries()) {
    const [summaryDate, metricKeyString] = groupKey.split(':')
    const metricKey = metricKeyString as AppleHealthMetricKey
    const ordered = [...groupSamples].sort((left, right) => left.end_at.localeCompare(right.end_at))
    const latest = ordered[ordered.length - 1]
    const numericValues = ordered
      .map((sample) => getSampleNumericValue(sample))
      .filter((value): value is number => value != null)

    if (metricKey === 'sleep_analysis') {
      const sleepHours = ordered.reduce((total, sample) => {
        if (!isSleepAsleepSample(sample)) {
          return total
        }

        const startAt = new Date(sample.start_at).getTime()
        const endAt = new Date(sample.end_at).getTime()

        if (!Number.isFinite(startAt) || !Number.isFinite(endAt) || endAt <= startAt) {
          return total
        }

        return total + (endAt - startAt) / 3_600_000
      }, 0)

      if (sleepHours > 0) {
        summaries.push({
          patient_id: patientId,
          connection_id: connectionId,
          summary_date: summaryDate,
          metric_key: 'sleep_duration',
          aggregation: 'daily_total',
          unit: 'h',
          value_numeric: Number(sleepHours.toFixed(2)),
          sample_count: ordered.length,
          summary_payload: {
            source_metric: 'sleep_analysis',
            asleep_segments: ordered.filter((sample) => isSleepAsleepSample(sample)).length
          }
        })
      }

      continue
    }

    if (DAILY_TOTAL_METRICS.has(metricKey)) {
      const total = numericValues.reduce((sum, value) => sum + value, 0)

      summaries.push({
        patient_id: patientId,
        connection_id: connectionId,
        summary_date: summaryDate,
        metric_key: metricKey,
        aggregation: 'daily_total',
        unit: latest.unit || null,
        value_numeric: Number(total.toFixed(4)),
        sample_count: ordered.length,
        summary_payload: {
          latest_text: latest.value_text || null
        }
      })

      continue
    }

    if (DAILY_AVERAGE_METRICS.has(metricKey) && numericValues.length > 0) {
      const total = numericValues.reduce((sum, value) => sum + value, 0)
      const average = total / numericValues.length
      const minimum = Math.min(...numericValues)
      const maximum = Math.max(...numericValues)

      summaries.push({
        patient_id: patientId,
        connection_id: connectionId,
        summary_date: summaryDate,
        metric_key: metricKey,
        aggregation: 'daily_average',
        unit: latest.unit || null,
        value_numeric: Number(average.toFixed(4)),
        sample_count: ordered.length,
        summary_payload: {
          min: minimum,
          max: maximum
        }
      })

      summaries.push({
        patient_id: patientId,
        connection_id: connectionId,
        summary_date: summaryDate,
        metric_key: metricKey,
        aggregation: 'daily_min',
        unit: latest.unit || null,
        value_numeric: Number(minimum.toFixed(4)),
        sample_count: ordered.length,
        summary_payload: {}
      })

      summaries.push({
        patient_id: patientId,
        connection_id: connectionId,
        summary_date: summaryDate,
        metric_key: metricKey,
        aggregation: 'daily_max',
        unit: latest.unit || null,
        value_numeric: Number(maximum.toFixed(4)),
        sample_count: ordered.length,
        summary_payload: {}
      })

      continue
    }

    if (DAILY_LATEST_METRICS.has(metricKey) || latest.sample_kind === 'category' || latest.sample_kind === 'electrocardiogram') {
      summaries.push({
        patient_id: patientId,
        connection_id: connectionId,
        summary_date: summaryDate,
        metric_key: metricKey,
        aggregation: 'latest',
        unit: latest.unit || null,
        value_numeric: getSampleNumericValue(latest),
        value_text: latest.value_text || null,
        sample_count: ordered.length,
        summary_payload: {
          latest_start_at: latest.start_at,
          latest_end_at: latest.end_at
        }
      })
    }
  }

  return summaries
}

export async function requestAppleHealthPermissions(
  patientId: string,
  metricKeys?: AppleHealthMetricKey[]
) {
  const requestedMetrics = normalizeMetricKeys(metricKeys)
  const bridgeMetrics = expandRequestedMetricKeys(metricKeys)
  const requestedAt = new Date().toISOString()

  if (!Capacitor.isPluginAvailable('AppleHealthBridge')) {
    return {
      status: 'error',
      granted_metrics: [] as AppleHealthMetricKey[],
      denied_metrics: requestedMetrics,
      requested_at: requestedAt
    } satisfies AppleHealthNativePermissionResult
  }

  const response = await AppleHealthBridge.requestPermissions({ metrics: bridgeMetrics })
  const grantedMetrics = normalizeStoredMetricKeys(response.granted_metrics)
  const deniedMetrics = normalizeStoredMetricKeys(response.denied_metrics)
  const status = normalizeAuthorizationStatus(response.status)

  await upsertAppleHealthConnection({
    patient_id: patientId,
    source_name: APPLE_HEALTH_CONNECTION_SOURCE,
    platform: 'ios',
    device_name: response.device_name || null,
    watch_name: response.watch_name || null,
    ios_app_version: response.ios_app_version || null,
    authorization_status: status,
    granted_metrics: grantedMetrics,
    revoked_metrics: deniedMetrics,
    last_authorized_at: response.requested_at || requestedAt,
    metadata: {
      last_permission_request_metrics: requestedMetrics
    }
  })

  return {
    status,
    granted_metrics: grantedMetrics,
    denied_metrics: deniedMetrics,
    requested_at: response.requested_at || requestedAt,
    device_name: response.device_name || null,
    watch_name: response.watch_name || null,
    ios_app_version: response.ios_app_version || null
  } satisfies AppleHealthNativePermissionResult
}

export async function syncAppleHealth(patientId: string, options: AppleHealthSyncOptions = {}) {
  const requestedMetrics = normalizeMetricKeys(options.metricKeys)
  const bridgeMetrics = expandRequestedMetricKeys(options.metricKeys)
  const connection = await getAppleHealthConnection(patientId)
  const connectionId = options.connectionId ?? connection?.id ?? null

  if (!Capacitor.isPluginAvailable('AppleHealthBridge')) {
    return {
      status: 'failed',
      imported_samples: 0,
      imported_summaries: 0,
      last_sync_at: new Date().toISOString(),
      last_cursor: null,
      summary: 'Bridge Apple Health indisponible dans cette execution.'
    } satisfies AppleHealthNativeSyncResult
  }

  const syncRun = await startAppleHealthSyncRun(patientId, requestedMetrics, connectionId)

  try {
    const lastCursor = options.lastCursor ?? (await getLatestSyncCursor(patientId))
    const response = await AppleHealthBridge.sync({
      metrics: bridgeMetrics,
      lastCursor
    })

    const samples = (response.samples || []).map((sample) =>
      normalizeSampleInput({
        ...sample,
        patient_id: patientId,
        connection_id: sample.connection_id || connectionId || null
      })
    )

    const summaries = (response.daily_summaries || []).map((summary) =>
      normalizeDailySummaryInput({
        ...summary,
        patient_id: patientId,
        connection_id: summary.connection_id || connectionId || null
      })
    )

    const autoSummaries = buildDerivedDailySummaries(patientId, connectionId, samples)
    const insertedSamples = await insertAppleHealthSamples(samples)
    const insertedSummaries = await upsertAppleHealthDailySummaries([...summaries, ...autoSummaries])
    const finalStatus = normalizeSyncStatus(response.status || 'completed')
    const lastSyncAt = normalizeDateTime(response.last_sync_at) || new Date().toISOString()

    await upsertAppleHealthConnection({
      patient_id: patientId,
      source_name: APPLE_HEALTH_CONNECTION_SOURCE,
      platform: connection?.platform || 'ios',
      device_name: response.device_name || connection?.device_name || null,
      watch_name: response.watch_name || connection?.watch_name || null,
      ios_app_version: response.ios_app_version || connection?.ios_app_version || null,
      authorization_status: connection?.authorization_status || 'pending',
      granted_metrics: connection?.granted_metrics || [],
      revoked_metrics: connection?.revoked_metrics || [],
      last_authorized_at: connection?.last_authorized_at || null,
      last_sync_at: lastSyncAt,
      metadata: {
        ...(connection?.metadata || {}),
        last_cursor: response.last_cursor || null
      }
    })

    if (syncRun) {
      await finishAppleHealthSyncRun(syncRun.id, {
        status: finalStatus,
        importedSamples: insertedSamples.length || response.imported_samples || 0,
        importedSummaries: insertedSummaries.length || response.imported_summaries || 0,
        lastCursor: response.last_cursor || null,
        metadata: {
          bridge_summary: response.summary || null
        }
      })
    }

    return {
      status: finalStatus,
      imported_samples: insertedSamples.length || response.imported_samples || 0,
      imported_summaries: insertedSummaries.length || response.imported_summaries || 0,
      last_sync_at: lastSyncAt,
      last_cursor: response.last_cursor || null,
      summary: response.summary || null,
      device_name: response.device_name || connection?.device_name || null,
      watch_name: response.watch_name || connection?.watch_name || null,
      ios_app_version: response.ios_app_version || connection?.ios_app_version || null
    } satisfies AppleHealthNativeSyncResult
  } catch (error) {
    if (syncRun) {
      await finishAppleHealthSyncRun(syncRun.id, {
        status: 'failed',
        importedSamples: 0,
        importedSummaries: 0,
        errorMessage: error instanceof Error ? error.message : String(error)
      })
    }

    throw new Error(buildErrorMessage('Synchronisation Apple Health impossible', error))
  }
}
